/**
 * CR460: Pipeline Trace + Diagnostics Modal
 *
 * Three-tab modal for inspecting Image Capture (S0.5) execution:
 *   Tab 1 — Timeline   (cr075_pipeline_trace_events)
 *   Tab 2 — Diagnostics (cr075_pipeline_diagnostics)
 *   Tab 3 — Handoff Checklist
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  RefreshCw,
  Clock,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { cn } from "@/lib/utils";

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

interface TraceEvent {
  id: string;
  created_at: string;
  batch_run_id: string;
  step_key: string;
  event_key: string;
  level: string;
  status: string;
  duration_ms: number | null;
  merchant_source_id: string | null;
  adapter_key: string | null;
  page_number: number | null;
  message: string | null;
  data_json: Record<string, unknown>;
}

interface DiagnosticsRow {
  id: string;
  created_at: string;
  updated_at: string;
  batch_run_id: string;
  step_key: string;
  merchant_source_id: string | null;
  adapter_key: string | null;
  status: string;
  diagnostics_json: Record<string, unknown>;
}

interface HandoffCheckResult {
  label: string;
  ok: boolean | null; // null = loading
  count: number | null;
  lastTs: string | null;
  detail: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

function levelVariant(level: string): "default" | "secondary" | "destructive" | "outline" | "warning" {
  if (level === "ERROR") return "destructive";
  if (level === "WARN") return "warning";
  return "secondary";
}

function statusDot(status: string) {
  if (status === "OK") return <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />;
  if (status === "FAILED") return <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />;
  if (status === "SKIPPED") return <AlertCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />;
  return <Clock className="h-3.5 w-3.5 text-warning shrink-0" />;
}

function CheckRow({ item }: { item: HandoffCheckResult }) {
  if (item.ok === null) {
    return (
      <div className="flex items-center gap-3 py-2 px-3 rounded-md border bg-muted/30">
        <Skeleton className="h-4 w-4 rounded-full" />
        <span className="text-sm text-muted-foreground">{item.label}</span>
      </div>
    );
  }
  return (
    <div
      className={cn(
        "flex items-start gap-3 py-2 px-3 rounded-md border",
        item.ok ? "bg-primary/5 border-primary/20" : "bg-destructive/5 border-destructive/20"
      )}
    >
      {item.ok ? (
        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{item.label}</span>
          {item.count !== null && (
            <Badge variant="secondary" className="text-xs h-4 px-1.5">
              {item.count}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{item.detail}</p>
        {item.lastTs && (
          <p className="text-xs text-muted-foreground/70 mt-0.5">
            Last: {formatDistanceToNow(new Date(item.lastTs), { addSuffix: true })}
          </p>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────────────────────────────────────

interface PipelineTraceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchRunId: string;
}

export function PipelineTraceModal({ open, onOpenChange, batchRunId }: PipelineTraceModalProps) {
  const [tab, setTab] = useState("timeline");

  // Timeline state
  const [events, setEvents] = useState<TraceEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [filterStep, setFilterStep] = useState<string>("ALL");
  const [filterLevel, setFilterLevel] = useState<string>("ALL");
  const [filterSource, setFilterSource] = useState<string>("ALL");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Diagnostics state
  const [diagnostics, setDiagnostics] = useState<DiagnosticsRow[]>([]);
  const [diagLoading, setDiagLoading] = useState(false);
  const [expandedDiag, setExpandedDiag] = useState<Set<string>>(new Set());

  // Handoff checklist state
  const [checklist, setChecklist] = useState<HandoffCheckResult[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(false);

  // ── Derived step keys from events
  const stepKeys = Array.from(new Set(events.map((e) => e.step_key)));
  const sourceIds = Array.from(
    new Set(events.map((e) => e.merchant_source_id).filter(Boolean) as string[])
  );

  // ── Load events
  const fetchEvents = useCallback(async () => {
    if (!batchRunId) return;
    setEventsLoading(true);
    try {
      const { data, error } = await supabase
        .from("cr075_pipeline_trace_events")
        .select("*")
        .eq("batch_run_id", batchRunId)
        .order("created_at", { ascending: true })
        .limit(500);
      if (!error) setEvents((data ?? []) as TraceEvent[]);
    } finally {
      setEventsLoading(false);
    }
  }, [batchRunId]);

  // ── Load diagnostics
  const fetchDiagnostics = useCallback(async () => {
    if (!batchRunId) return;
    setDiagLoading(true);
    try {
      const { data, error } = await supabase
        .from("cr075_pipeline_diagnostics")
        .select("*")
        .eq("batch_run_id", batchRunId)
        .order("updated_at", { ascending: false });
      if (!error) setDiagnostics((data ?? []) as DiagnosticsRow[]);
    } finally {
      setDiagLoading(false);
    }
  }, [batchRunId]);

  // ── Load handoff checklist
  const fetchChecklist = useCallback(async () => {
    if (!batchRunId) return;
    setChecklistLoading(true);
    const results: HandoffCheckResult[] = [
      { label: "Pages persisted (render_storage_path set)", ok: null, count: null, lastTs: null, detail: "Checking cr075_flyer_page_truth…" },
      { label: "Manifest written", ok: null, count: null, lastTs: null, detail: "Checking flyer_image_capture_manifests…" },
      { label: "S1 work enqueued (step attempts)", ok: null, count: null, lastTs: null, detail: "Checking cr075_batch_run_step_attempts for S1_2_OCR…" },
      { label: "Truth table rows exist", ok: null, count: null, lastTs: null, detail: "Checking cr075_flyer_page_truth row count…" },
    ];
    setChecklist([...results]);

    try {
      const [pagesRes, manifestRes, attemptsRes, truthRes] = await Promise.all([
        supabase
          .from("cr075_flyer_page_truth")
          .select("id, rendered_at", { count: "exact" })
          .eq("batch_run_id", batchRunId)
          .not("render_storage_path", "is", null)
          .order("rendered_at", { ascending: false })
          .limit(1),
        supabase
          .from("flyer_image_capture_manifests")
          .select("id, created_at", { count: "exact" })
          .eq("batch_run_id", batchRunId)
          .order("created_at", { ascending: false })
          .limit(1),
        supabase
          .from("cr075_batch_run_step_attempts")
          .select("id, created_at", { count: "exact" })
          .eq("batch_run_id", batchRunId)
          .like("step_key", "S1%")
          .order("created_at", { ascending: false })
          .limit(1),
        supabase
          .from("cr075_flyer_page_truth")
          .select("id, created_at", { count: "exact" })
          .eq("batch_run_id", batchRunId)
          .order("created_at", { ascending: false })
          .limit(1),
      ]);

      const pagesCount = pagesRes.count ?? 0;
      const manifestCount = manifestRes.count ?? 0;
      const attemptsCount = attemptsRes.count ?? 0;
      const truthCount = truthRes.count ?? 0;

      setChecklist([
        {
          label: "Pages persisted (render_storage_path set)",
          ok: pagesCount > 0,
          count: pagesCount,
          lastTs: pagesRes.data?.[0]?.rendered_at ?? null,
          detail:
            pagesCount > 0
              ? `${pagesCount} page(s) have a render_storage_path.`
              : "No pages with render_storage_path. Capture may have failed before image upload.",
        },
        {
          label: "Manifest written",
          ok: manifestCount > 0,
          count: manifestCount,
          lastTs: manifestRes.data?.[0]?.created_at ?? null,
          detail:
            manifestCount > 0
              ? `${manifestCount} manifest row(s) found.`
              : "No manifest row. image-capture-manifest-complete may not have been called or failed.",
        },
        {
          label: "S1 work enqueued (step attempts)",
          ok: attemptsCount > 0,
          count: attemptsCount,
          lastTs: attemptsRes.data?.[0]?.created_at ?? null,
          detail:
            attemptsCount > 0
              ? `${attemptsCount} S1 step attempt(s) created.`
              : "No S1 attempts. OCR enqueue may not have been triggered.",
        },
        {
          label: "Truth table rows exist",
          ok: truthCount > 0,
          count: truthCount,
          lastTs: truthRes.data?.[0]?.created_at ?? null,
          detail:
            truthCount > 0
              ? `${truthCount} total truth table row(s) for this run.`
              : "No truth table rows at all. Sentinel page registration may have failed.",
        },
      ]);
    } catch {
      // leave loading state cleared
    } finally {
      setChecklistLoading(false);
    }
  }, [batchRunId]);

  // ── Fetch on open
  useEffect(() => {
    if (open && batchRunId) {
      fetchEvents();
      fetchDiagnostics();
      fetchChecklist();
    }
  }, [open, batchRunId, fetchEvents, fetchDiagnostics, fetchChecklist]);

  // ── Filtered events
  const filteredEvents = events.filter((e) => {
    if (filterStep !== "ALL" && e.step_key !== filterStep) return false;
    if (filterLevel !== "ALL" && e.level !== filterLevel) return false;
    if (filterSource !== "ALL" && e.merchant_source_id !== filterSource) return false;
    return true;
  });

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleDiag = (id: string) => {
    setExpandedDiag((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleRefresh = () => {
    fetchEvents();
    fetchDiagnostics();
    fetchChecklist();
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-full h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base font-semibold">
              Pipeline Trace &amp; Diagnostics
              <span className="ml-2 font-mono text-xs text-muted-foreground font-normal">
                {batchRunId.slice(0, 8)}…
              </span>
            </DialogTitle>
            <Button variant="ghost" size="sm" onClick={handleRefresh} className="h-7 px-2">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Refresh
            </Button>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-6 mt-3 shrink-0 w-fit">
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="diagnostics">Diagnostics</TabsTrigger>
            <TabsTrigger value="handoff">Handoff Checklist</TabsTrigger>
          </TabsList>

          {/* ─── TAB 1: TIMELINE ─────────────────────────────────────────── */}
          <TabsContent value="timeline" className="flex-1 flex flex-col min-h-0 px-6 pb-5 mt-3">
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-3 shrink-0">
              <Select value={filterStep} onValueChange={setFilterStep}>
                <SelectTrigger className="h-8 w-40 text-xs">
                  <SelectValue placeholder="Step" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Steps</SelectItem>
                  <SelectItem value="S0_5">S0.5 (Image Capture)</SelectItem>
                  {stepKeys
                    .filter((k) => k !== "S0_5")
                    .map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <Select value={filterLevel} onValueChange={setFilterLevel}>
                <SelectTrigger className="h-8 w-32 text-xs">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Levels</SelectItem>
                  <SelectItem value="INFO">INFO</SelectItem>
                  <SelectItem value="WARN">WARN</SelectItem>
                  <SelectItem value="ERROR">ERROR</SelectItem>
                </SelectContent>
              </Select>

              {sourceIds.length > 0 && (
                <Select value={filterSource} onValueChange={setFilterSource}>
                  <SelectTrigger className="h-8 w-52 text-xs">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Sources</SelectItem>
                    {sourceIds.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s.slice(0, 8)}…
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <span className="text-xs text-muted-foreground self-center ml-auto">
                {filteredEvents.length} event{filteredEvents.length !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Timeline table */}
            <div className="flex-1 overflow-auto rounded-md border">
              {eventsLoading ? (
                <div className="p-4 space-y-2">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground text-sm">
                  <AlertCircle className="h-8 w-8 mb-2 opacity-40" />
                  {events.length === 0
                    ? "No trace events found for this run yet."
                    : "No events match the current filters."}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-36 text-xs">Time</TableHead>
                      <TableHead className="w-32 text-xs">Event</TableHead>
                      <TableHead className="w-16 text-xs">Level</TableHead>
                      <TableHead className="w-16 text-xs">Status</TableHead>
                      <TableHead className="w-16 text-xs">ms</TableHead>
                      <TableHead className="w-24 text-xs">Adapter</TableHead>
                      <TableHead className="w-12 text-xs">Pg</TableHead>
                      <TableHead className="text-xs">Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEvents.map((event) => {
                      const isExpanded = expandedRows.has(event.id);
                      const hasData =
                        event.data_json &&
                        typeof event.data_json === "object" &&
                        Object.keys(event.data_json).length > 0;
                      return (
                        <>
                          <TableRow
                            key={event.id}
                            className={cn(
                              "cursor-pointer hover:bg-muted/50",
                              event.level === "ERROR" && "bg-destructive/5"
                            )}
                            onClick={() => hasData && toggleRow(event.id)}
                          >
                            <TableCell className="text-xs font-mono whitespace-nowrap py-2 text-muted-foreground">
                              {format(new Date(event.created_at), "HH:mm:ss.SSS")}
                            </TableCell>
                            <TableCell className="text-xs font-mono py-2 max-w-32 truncate" title={event.event_key}>
                              {event.event_key}
                            </TableCell>
                            <TableCell className="py-2">
                              <Badge variant={levelVariant(event.level)} className="text-[10px] px-1.5 h-4">
                                {event.level}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-2">
                              <div className="flex items-center gap-1">
                                {statusDot(event.status)}
                                <span className="text-xs">{event.status}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs font-mono py-2 text-muted-foreground">
                              {event.duration_ms != null ? event.duration_ms : "—"}
                            </TableCell>
                            <TableCell className="text-xs py-2 max-w-24 truncate text-muted-foreground" title={event.adapter_key ?? undefined}>
                              {event.adapter_key ?? "—"}
                            </TableCell>
                            <TableCell className="text-xs py-2 text-center text-muted-foreground">
                              {event.page_number != null ? event.page_number : "—"}
                            </TableCell>
                            <TableCell className="text-xs py-2">
                              <div className="flex items-center gap-1">
                                <span className="truncate max-w-xs" title={event.message ?? undefined}>
                                  {event.message ?? <span className="text-muted-foreground/50">—</span>}
                                </span>
                                {hasData && (
                                  <ChevronDown
                                    className={cn(
                                      "h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform",
                                      isExpanded && "rotate-180"
                                    )}
                                  />
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          {isExpanded && hasData && (
                            <TableRow key={`${event.id}-expanded`} className="bg-muted/20">
                              <TableCell colSpan={8} className="px-4 py-2">
                                <pre className="text-xs font-mono text-muted-foreground overflow-auto max-h-48 whitespace-pre-wrap">
                                  {JSON.stringify(event.data_json, null, 2)}
                                </pre>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* ─── TAB 2: DIAGNOSTICS ──────────────────────────────────────── */}
          <TabsContent value="diagnostics" className="flex-1 overflow-auto px-6 pb-5 mt-3 space-y-3">
            {diagLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-28 w-full" />
                ))}
              </div>
            ) : diagnostics.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-muted-foreground text-sm">
                <AlertCircle className="h-8 w-8 mb-2 opacity-40" />
                No diagnostics snapshots found for this run.
              </div>
            ) : (
              diagnostics.map((diag) => {
                const isExpanded = expandedDiag.has(diag.id);
                const dj = diag.diagnostics_json as Record<string, unknown>;

                return (
                  <Collapsible key={diag.id} open={isExpanded} onOpenChange={() => toggleDiag(diag.id)}>
                    <div className="rounded-md border bg-card">
                      <CollapsibleTrigger asChild>
                        <button className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors rounded-t-md">
                          <div className="flex items-center gap-3 min-w-0">
                            <Badge
                              variant={
                                diag.status === "ERROR"
                                  ? "destructive"
                                  : diag.status === "OK"
                                  ? "outline"
                                  : "secondary"
                              }
                              className="text-xs shrink-0"
                            >
                              {diag.status}
                            </Badge>
                            <span className="text-sm font-mono font-medium">{diag.step_key}</span>
                            {diag.adapter_key && (
                              <span className="text-xs text-muted-foreground">
                                adapter: {diag.adapter_key}
                              </span>
                            )}
                            {diag.merchant_source_id && (
                              <span className="text-xs text-muted-foreground font-mono">
                                src: {diag.merchant_source_id.slice(0, 8)}…
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(diag.updated_at), { addSuffix: true })}
                            </span>
                            <ChevronDown
                              className={cn(
                                "h-4 w-4 text-muted-foreground transition-transform",
                                isExpanded && "rotate-180"
                              )}
                            />
                          </div>
                        </button>
                      </CollapsibleTrigger>

                      {/* Quick key-value summary */}
                      <div className="px-4 pb-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs border-t">
                        {[
                          ["adapter_key", dj.adapter_key],
                          ["source_url", dj.source_url],
                          ["pages_total", dj.pages_total],
                          ["pages_persisted", dj.pages_persisted],
                          ["last_error", dj.last_error],
                          ["postal_code", dj.postal_code],
                          ["cookie_strategy", dj.cookie_strategy],
                          ["region", dj.region],
                        ]
                          .filter(([, v]) => v !== undefined && v !== null && v !== "")
                          .map(([k, v]) => (
                            <div key={k as string} className="mt-2">
                              <span className="text-muted-foreground font-medium">{k as string}</span>
                              <div
                                className={cn(
                                  "truncate mt-0.5",
                                  k === "last_error" && "text-destructive"
                                )}
                                title={String(v)}
                              >
                                {String(v)}
                              </div>
                            </div>
                          ))}
                      </div>

                      <CollapsibleContent>
                        <div className="px-4 pb-3 border-t">
                          <p className="text-xs text-muted-foreground mb-1 pt-2">Full diagnostics_json</p>
                          <pre className="text-xs font-mono bg-muted/40 rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap">
                            {JSON.stringify(diag.diagnostics_json, null, 2)}
                          </pre>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })
            )}
          </TabsContent>

          {/* ─── TAB 3: HANDOFF CHECKLIST ────────────────────────────────── */}
          <TabsContent value="handoff" className="flex-1 overflow-auto px-6 pb-5 mt-3">
            <div className="max-w-xl space-y-2">
              <p className="text-sm text-muted-foreground mb-4">
                Verifies the four conditions that must all be true for a valid S0.5 → S1.2 handoff.
              </p>
              {checklistLoading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 py-2 px-3 rounded-md border bg-muted/30"
                    >
                      <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                      <Skeleton className="h-4 flex-1" />
                    </div>
                  ))
                : checklist.map((item) => <CheckRow key={item.label} item={item} />)}

              {!checklistLoading && checklist.length > 0 && (
              <div
                  className={cn(
                    "mt-4 rounded-md px-4 py-3 text-sm font-medium border",
                    checklist.every((c) => c.ok)
                      ? "bg-primary/10 border-primary/30 text-primary"
                      : "bg-destructive/10 border-destructive/30 text-destructive"
                  )}
                >
                  {checklist.every((c) => c.ok)
                    ? "✅ All handoff conditions satisfied — S0.5 → S1.2 transition looks valid."
                    : `❌ ${checklist.filter((c) => !c.ok).length} condition(s) not met — handoff is incomplete.`}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
